import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const orderId = params.get('order_id');
  const [status, setStatus] = useState('polling');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!orderId) return;
    let mounted = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/cashfree/status/${orderId}`);
        if (!mounted) return;
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.payment_status === 'expired' || data.status === 'failed') { setStatus('failed'); return; }
        setAttempts((a) => a + 1);
      } catch {
        setAttempts((a) => a + 1);
      }
    };
    poll();
    const interval = setInterval(() => {
      if (attempts > 20) { setStatus('failed'); clearInterval(interval); return; }
      poll();
    }, 2000);
    return () => { mounted = false; clearInterval(interval); };
  }, [orderId, attempts]);

  return (
    <div className="pb-24 md:pb-8 min-h-[60vh] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white rounded-3xl p-8 shadow-card border border-borderbeige text-center"
      >
        {status === 'polling' && (
          <>
            <Loader2 className="w-16 h-16 text-saffron mx-auto mb-4 animate-spin" />
            <h1 className="font-heading font-bold text-2xl text-textprimary mb-2">Confirming payment...</h1>
            <p className="text-textsecondary font-body text-sm">Hold tight, this only takes a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
              <CheckCircle2 className="w-20 h-20 text-fresh mx-auto mb-4" />
            </motion.div>
            <h1 className="font-heading font-extrabold text-3xl text-fresh mb-2">Payment Successful!</h1>
            <p className="text-textsecondary font-body mb-6">Your order has been placed. We're already firing up the tandoor.</p>
            <Link to={`/orders/${orderId}`} data-testid="view-order-btn" className="inline-block bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-bold shadow-warm">
              Track My Order
            </Link>
          </>
        )}
        {status === 'failed' && (
          <>
            <XCircle className="w-20 h-20 text-maroon mx-auto mb-4" />
            <h1 className="font-heading font-bold text-2xl text-maroon mb-2">Payment failed</h1>
            <p className="text-textsecondary font-body mb-6">Please try again or use Cash on Delivery.</p>
            <Link to={`/orders/${orderId}`} className="inline-block bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-bold">
              Go to Order Details
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-card text-center">
        <XCircle className="w-16 h-16 text-maroon mx-auto mb-4" />
        <h1 className="font-heading font-bold text-2xl text-textprimary mb-2">Payment cancelled</h1>
        <p className="text-textsecondary font-body mb-6">No worries — your order was not placed.</p>
        <Link to="/cart" className="inline-block bg-saffron text-white px-6 py-3 rounded-2xl font-heading font-bold">Back to Cart</Link>
      </div>
    </div>
  );
}
